import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { photoUrl } from '@/lib/photos'
import { cardFits, sheetFacts, sheetText, type CardFit, type SheetStyle } from '@/lib/recipeSheet'
import { cardPhotos, type Recipe } from '@/lib/recipes'
import { cn } from '@/lib/utils'

// A recipe typeset for paper: a full page laid out like a menu, or a note card (with a back when it needs one).
// What's on screen is the preview; a copy at true size waits beside the app for the printer (see recipe-sheet.css).
export function RecipeSheet({ recipe, style, original }: { recipe: Recipe; style: SheetStyle; original: boolean }) {
  const text = sheetText(recipe)
  const hasBack = text.ingredients.length > 0 && (text.steps.length > 0 || !!text.notes)
  const fit = useCardFit(recipe, hasBack, style === 'card')
  const photo = original ? cardPhotos(recipe)[0] : undefined
  const sheets = (measure: boolean) =>
    style === 'page' ? (
      <Page recipe={recipe} photo={photo} />
    ) : (
      <Card recipe={recipe} fit={fit.value} measure={measure ? fit.measure : undefined} />
    )
  return (
    <>
      <div className="flex flex-col gap-6">{sheets(true)}</div>
      {createPortal(
        <div className="sheet-print hidden" aria-hidden>
          {/* Paper size for the browser's print dialog. A card is printed edge to edge; a page keeps its margins on
              every page it runs to. */}
          <style>{`@page { size: ${style === 'page' ? 'letter; margin: 0.75in 0' : '6in 4in; margin: 0'}; }`}</style>
          {sheets(false)}
        </div>,
        document.body,
      )}
    </>
  )
}

function Facts({ recipe }: { recipe: Recipe }) {
  const facts = sheetFacts(recipe)
  if (!facts.length) return null
  return (
    <p className="sheet-meta">
      {facts.map(([label, value]) => (
        <span key={label}>
          <span className="sheet-label">{label}</span> {value}
        </span>
      ))}
    </p>
  )
}

function Ingredients({ items, spread, heading }: { items: string[]; spread?: boolean; heading: string }) {
  return (
    <section>
      <h2 className="sheet-heading">{heading}</h2>
      <ul className={cn('sheet-list', spread && 'is-spread')}>
        {items.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
    </section>
  )
}

// One step reads as a paragraph; several are numbered.
function Steps({ items }: { items: string[] }) {
  if (items.length === 1) return <p className="sheet-prose">{items[0]}</p>
  return (
    <ol className="sheet-steps">
      {items.map((line, i) => (
        <li key={i}>{line}</li>
      ))}
    </ol>
  )
}

function Page({ recipe, photo }: { recipe: Recipe; photo?: Recipe['photos'][number] }) {
  const { ingredients, steps, notes } = sheetText(recipe)
  const right = steps.length > 0 || !!notes
  return (
    <div className="sheet sheet-page" data-testid="sheet-page">
      <div className="sheet-inner">
        {photo && (
          <figure className="sheet-original">
            <img
              src={photoUrl(photo.ref, 'display')}
              width={photo.width}
              height={photo.height}
              alt={`The original card for ${recipe.title}`}
            />
          </figure>
        )}
        <h1 className="sheet-title">{recipe.title}</h1>
        <Facts recipe={recipe} />
        {(ingredients.length > 0 || right) && (
          <div className={cn('sheet-columns', !(ingredients.length && right) && 'is-single')}>
            {ingredients.length > 0 && (
              <Ingredients items={ingredients} heading="Ingredients" spread={!right && ingredients.length > 8} />
            )}
            {right && (
              <div>
                {steps.length > 0 && (
                  <section>
                    <h2 className="sheet-heading">Directions</h2>
                    <Steps items={steps} />
                  </section>
                )}
                {notes && (
                  <section className={cn(steps.length > 0 && 'sheet-notes')}>
                    <h2 className="sheet-heading">Notes</h2>
                    <p className="sheet-prose">{notes}</p>
                  </section>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function Card({ recipe, fit, measure }: { recipe: Recipe; fit: CardFit; measure?: (el: HTMLElement | null) => void }) {
  const { ingredients, steps, notes } = sheetText(recipe)
  const right = steps.length > 0 || !!notes
  const css = { '--fit': fit.scale } as CSSProperties
  const inner = cn('sheet-inner', fit.grown && 'h-auto! min-h-[calc(var(--in)*4)]')
  const directions = (ruled: boolean) => (
    <>
      {steps.length > 0 && (
        <section>
          <h2 className={cn('sheet-heading', ruled && 'is-ruled')}>Directions</h2>
          <Steps items={steps} />
        </section>
      )}
      {notes && (
        <section className={cn(steps.length > 0 && 'sheet-notes')}>
          <h2 className={cn('sheet-heading', ruled && 'is-ruled')}>Notes</h2>
          <p className="sheet-prose">{notes}</p>
        </section>
      )}
    </>
  )
  return (
    <>
      <div className="sheet sheet-card" style={css} data-testid="sheet-card">
        <div className={inner} ref={measure}>
          <h1 className="sheet-title">{recipe.title}</h1>
          <Facts recipe={recipe} />
          {(ingredients.length > 0 || right) && (
            <div className={cn('sheet-columns', (fit.back || !(ingredients.length && right)) && 'is-single')}>
              {ingredients.length > 0 && (
                <Ingredients items={ingredients} heading="Ingredients" spread={fit.back || !right} />
              )}
              {!fit.back && right && <div>{directions(false)}</div>}
            </div>
          )}
        </div>
      </div>
      {fit.back && (
        <div className="sheet sheet-card" style={css} data-testid="sheet-card-back">
          <div className={inner} ref={measure}>
            {directions(true)}
          </div>
        </div>
      )}
    </>
  )
}

// Tries each way of fitting the recipe on a card, in order, until nothing spills over its edge. Sizes are fixed
// shares of the card's width, so what fits in the preview fits on paper too.
function useCardFit(recipe: Recipe, hasBack: boolean, active: boolean) {
  const fits = cardFits(hasBack)
  const [step, setStep] = useState(0)
  const boxes = useRef(new Set<HTMLElement>())
  const key = `${recipe.title}\n${recipe.ingredients}\n${recipe.steps}\n${recipe.notes}\n${hasBack}`
  const [seen, setSeen] = useState(key)
  if (seen !== key) {
    setSeen(key)
    setStep(0)
  }

  useLayoutEffect(() => {
    if (!active) return
    const spills = [...boxes.current].some((el) => el.isConnected && el.scrollHeight > el.clientHeight + 1)
    if (spills && step < fits.length - 1) setStep(step + 1)
  }, [active, step, key, fits.length])

  // The first measure can come before the font has loaded; measure again from the start once it has.
  useLayoutEffect(() => {
    let live = true
    void document.fonts?.ready.then(() => live && setStep(0))
    return () => {
      live = false
    }
  }, [])

  const measure = (el: HTMLElement | null) => {
    if (el) boxes.current.add(el)
  }
  return { value: fits[Math.min(step, fits.length - 1)]!, measure }
}
